import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MuiLink,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  LockOpen as LockOpenIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import sessionService from "../../services/session.service";
import apiService from "../../services/api.service";
import { AuthContext } from "../../contexts/AuthContext";
import { AE_palette } from "../../style/theme";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { createDdsPlsCatalogue } from "../../services/data/adapters/DdsPlsCatalogue";
import { getDDSClient } from "../../lib/ddsClient";
import PlsCompetitionFixturePicker from "../../components/Renderer/PlsCompetitionFixturePicker";
import { useAlert } from "../../contexts/AlertContext";
import { toDateTimeLocalValue } from "../../utils/dateTimeLocal";
import { SESSION_NAME_MAX_LENGTH, getSessionDisplayName, validateSessionName } from "../../utils/sessionName";
import MaestroSetupFields from "../../components/Session/MaestroSetupFields";
import {
  fieldsInGroup,
  formatFieldValue,
  supportsMaestro,
  maestroFormFromSession,
  previewRendererUrlFor,
  retargetPreviewUrl,
  toMaestroPayload,
  sessionMaestroPayload,
  isSameMaestro,
  validateMaestroForm,
  fieldsForFlavour,
  previewSourceLabel,
} from "../../utils/maestroSettings";
import { useSessionFields } from "../../hooks/useSessionFields";

const SESSION_BASE_URL =
  process.env.REACT_APP_SESSION_API_URL || "http://localhost:3005";
const buildShortUrl = (slug) => `${SESSION_BASE_URL}/${slug}`;

const STATUS_COLORS = {
  active: "success",
  locked: "warning",
  inactive: "default",
  free: "info",
  deleted: "error",
};

const FLAVOUR_LABELS = {
  aedrive: "AeDrive",
  aether: "Aether",
  copilot: "Copilot",
  monitor: "Monitor",
  custom: "Custom",
};

const InfoRow = ({ label, value, mono }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" display="block" color="textSecondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={mono ? { fontFamily: "monospace", wordBreak: "break-all" } : {}}
    >
      {value || "—"}
    </Typography>
  </Box>
);

// Read-only list of one Maestro field group, e.g. "Locked for operators".
// Only enabled fields are listed; a field the session has no value for
// (added after it was saved) shows "Not set".
const MaestroFieldSummary = ({ title, color, fields, values }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" display="block" sx={{ color, fontWeight: 600, mb: 0.5 }}>
      {title}
    </Typography>
    {fields.map((field) => {
      const isSet = Boolean(values) && Object.prototype.hasOwnProperty.call(values, field.fieldName);
      const value = values?.[field.fieldName];
      const highlighted = isSet && field.fieldType === "boolean" && value;
      return (
        <Box key={field.fieldName} sx={{ display: "flex", justifyContent: "space-between", gap: 2, maxWidth: 420 }}>
          <Typography variant="body2">{field.label}</Typography>
          <Typography variant="body2" sx={{ color: highlighted ? "var(--green)" : "text.secondary" }}>
            {isSet ? formatFieldValue(field, value) : "Not set"}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const SessionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useContext(AuthContext);
  const alert = useAlert();

  const rawRole = userInfo?.data?.role;
  const userRole = typeof rawRole === "object" && rawRole !== null
    ? rawRole.name
    : rawRole;
  const isElevated = ["super_admin", "admin", "supervisor"].includes(userRole);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rendererMap, setRendererMap] = useState({}); // rendererId → name
  const [userMap, setUserMap] = useState({}); // userId → display name
  const [competitionName, setCompetitionName] = useState(null);
  const [seasonName, setSeasonName] = useState(null);
  const [fixtureInfoLoading, setFixtureInfoLoading] = useState(false);
  const [orgName, setOrgName] = useState(
    userInfo?.data?.organization_id?.name || null
  );
  // Sessions available for the breadcrumb session-switcher dropdown.
  const [orgSessions, setOrgSessions] = useState([]);

  // ─── Delete dialog ────────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ─── Rename ───────────────────────────────────────────────────────────────
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [nameError, setNameError] = useState(null);
  const [nameSaving, setNameSaving] = useState(false);

  // ─── Edit scene config ────────────────────────────────────────────────────
  const [editingConfig, setEditingConfig] = useState(false);
  const [renderers, setRenderers] = useState([]);
  // Org renderer list fetched with the page — the edit form reuses it.
  const [orgRenderers, setOrgRenderers] = useState([]);
  // Configured Maestro setup fields (global)
  const maestroFields = fieldsForFlavour(useSessionFields(), session?.applicationFlavour);
  const [configLoading, setConfigLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    rendererId: "",
    fixtureId: "",
    fixtureName: "",
    fixtureStartTime: "",
    fixtureEndTime: "",
  });
  // ─── Edit Maestro setup ───────────────────────────────────────────────────
  const [editingMaestro, setEditingMaestro] = useState(false);
  const [maestroForm, setMaestroForm] = useState(() => maestroFormFromSession(null));
  const [maestroErrors, setMaestroErrors] = useState({});
  const [maestroSaving, setMaestroSaving] = useState(false);
  // Competition/season browse state for the fixture picker in the edit form —
  // fixture start/end times are always derived from the picker's selection
  // (see PlsCompetitionFixturePicker's onSelect below), never typed manually,
  // mirroring SessionManagement.jsx's create-dialog picker.
  const [editPlsCompetitions, setEditPlsCompetitions] = useState([]);
  const [editPlsCompetitionId, setEditPlsCompetitionId] = useState("");
  const [editPlsSeasons, setEditPlsSeasons] = useState([]);
  const [editPlsSeasonId, setEditPlsSeasonId] = useState("");
  const editFixtureCatalogue = useMemo(
    () => createDdsPlsCatalogue({
      client: getDDSClient(),
      competitionId: editPlsCompetitionId,
      seasonId: editPlsSeasonId,
    }),
    [editPlsCompetitionId, editPlsSeasonId],
  );

  // ─── Short URL ────────────────────────────────────────────────────────────
  const [shortUrlLoading, setShortUrlLoading] = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const [existingAliases, setExistingAliases] = useState([]);
  const [aliasesLoading, setAliasesLoading] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState("");
  const [deleteAliasConfirmOpen, setDeleteAliasConfirmOpen] = useState(false);

  const showSnackbar = (message, severity = "success") => {
    if (severity === "error") alert.error(message);
    else alert.success(message);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await sessionService.getSessionById(id);
        const sessionData = res.data;
        setSession(sessionData);

        // Resolve renderer names for display
        const orgField = userInfo?.data?.organization_id;
        const orgId =
          typeof orgField === "object" && orgField !== null
            ? orgField._id
            : orgField;
        if (orgId) {
          const renderersRes = await apiService
            .getRenderersByOrganization(orgId)
            .catch(() => null);
          const renderersList =
            renderersRes?.data?.renderers || renderersRes?.renderers || [];
          setOrgRenderers(renderersList);
          setRendererMap(
            Object.fromEntries(
              renderersList.map((r) => [
                r._id || r.id,
                r.name || r._id || r.id,
              ]),
            ),
          );
        }

        // Resolve organisation name — use cached value from userInfo if the org matches,
        // otherwise fetch (super_admin viewing another org's session)
        const sessionOrgId = sessionData?.organisationId;
        const userOrgId = userInfo?.data?.organization_id?._id || userInfo?.data?.organization_id;
        const userOrgName = userInfo?.data?.organization_id?.name;
        if (sessionOrgId && sessionOrgId !== userOrgId) {
          apiService.getOrganizationById(sessionOrgId)
            .then((r) => {
              const org = r?.data || r;
              const name = org?.name || org?.organisation_name || org?.organizationName;
              if (name) setOrgName(name);
            })
            .catch(() => { /* fall back to raw ID */ });
        } else if (userOrgName) {
          setOrgName(userOrgName);
        }

        // Build userId → display name map from org users (for legacy createdBy ObjectIds)
        if (orgId) {
          apiService
            .getUsersByOrganization(orgId)
            .then((r) => {
              const users = r?.data?.users || r?.data || r?.users || [];
              const map = {};
              for (const u of users) {
                const id = u._id || u.id;
                const name = u.user_name || u.username || u.name || u.email;
                if (id && name) map[id] = name;
              }
              setUserMap(map);
            })
            .catch(() => {
              /* fall back to raw createdBy value */
            });
        }
      } catch (err) {
        setError(err.message || "Failed to load session");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Sessions for the breadcrumb switcher dropdown — mirrors SessionManagement's listing choice.
  useEffect(() => {
    const fn = isElevated ? sessionService.listOrgSessions : sessionService.listMySessions;
    fn({ page: 1, limit: 200 })
      .then((res) => setOrgSessions(res.data || []))
      .catch(() => setOrgSessions([]));
  }, [isElevated]);

  // Competition/season names are stored in applicationDetails alongside their
  // ids when a session is created or its fixture changes. Older sessions only
  // carry the ids, so resolve just the missing name(s) from the DDS PLS
  // catalogue; a row shows a loader only while its own name is being looked up.
  const {
    competitionId: sessionCompetitionId,
    seasonId: sessionSeasonId,
    competitionName: storedCompetitionName,
    seasonName: storedSeasonName,
  } = session?.applicationDetails || {};
  useEffect(() => {
    setCompetitionName(storedCompetitionName || null);
    setSeasonName(storedSeasonName || null);
    const needCompetition = Boolean(sessionCompetitionId && !storedCompetitionName);
    const needSeason = Boolean(sessionSeasonId && !storedSeasonName);
    if (!needCompetition && !needSeason) {
      setFixtureInfoLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFixtureInfoLoading(true);
    const catalogue = createDdsPlsCatalogue({ client: getDDSClient() });
    Promise.allSettled([
      needCompetition ? catalogue.getCompetitions() : Promise.resolve([]),
      needSeason ? catalogue.getSeasons(sessionCompetitionId) : Promise.resolve([]),
    ]).then(([compsResult, seasonsResult]) => {
      if (cancelled) return;
      const comps = compsResult.status === "fulfilled" ? compsResult.value : [];
      const seasons = seasonsResult.status === "fulfilled" ? seasonsResult.value : [];
      if (needCompetition) setCompetitionName(comps.find((c) => c.id === sessionCompetitionId)?.name || null);
      if (needSeason) setSeasonName(seasons.find((s) => s.id === sessionSeasonId)?.name || null);
      setFixtureInfoLoading(false);
    });
    return () => { cancelled = true; };
  }, [sessionCompetitionId, sessionSeasonId, storedCompetitionName, storedSeasonName]);

  // ─── Edit config handlers ─────────────────────────────────────────────────

  const handleOpenEdit = async () => {
    setEditForm({
      rendererId: session.rendererId || "",
      fixtureId: session.fixtureId || "",
      fixtureName: session.fixtureName || "",
      fixtureStartTime: toDateTimeLocalValue(session.fixtureStartTime),
      fixtureEndTime: toDateTimeLocalValue(session.fixtureEndTime),
    });
    const seededCompetitionId = session.applicationDetails?.competitionId || "";
    const seededSeasonId = session.applicationDetails?.seasonId || "";
    setEditPlsCompetitionId(seededCompetitionId);
    setEditPlsSeasonId(seededSeasonId);
    setEditingConfig(true);

    // Competition/season lists load in the background — the picker already
    // shows the session's stored names, so nothing waits on them.
    const editCatalogue = createDdsPlsCatalogue({ client: getDDSClient() });
    editCatalogue.getCompetitions().catch(() => []).then(setEditPlsCompetitions);
    if (seededCompetitionId) {
      editCatalogue.getSeasons(seededCompetitionId).catch(() => []).then(setEditPlsSeasons);
    }

    // Renderers come from the page load; only fetch if that came back empty.
    if (orgRenderers.length) {
      setRenderers(orgRenderers);
      return;
    }
    setConfigLoading(true);
    try {
      const orgField = userInfo?.data?.organization_id;
      const orgId =
        typeof orgField === "object" && orgField !== null
          ? orgField._id
          : orgField;
      const renderersRes = await apiService
        .getRenderersByOrganization(orgId)
        .catch(() => null);
      const renderersList =
        renderersRes?.data?.renderers || renderersRes?.renderers || [];
      setRenderers(renderersList);
      setOrgRenderers(renderersList);
      setRendererMap(
        Object.fromEntries(
          renderersList.map((r) => [r._id || r.id, r.name || r._id || r.id]),
        ),
      );
    } finally {
      setConfigLoading(false);
    }
  };

  const handleEditCompetitionChange = async (competitionId) => {
    setEditPlsCompetitionId(competitionId);
    setEditPlsSeasonId("");
    setEditForm((f) => ({ ...f, fixtureId: "", fixtureName: "", fixtureStartTime: "", fixtureEndTime: "" }));
    const editCatalogue = createDdsPlsCatalogue({ client: getDDSClient() });
    const seasons = await editCatalogue.getSeasons(competitionId).catch(() => []);
    setEditPlsSeasons(seasons);
  };

  const handleEditSeasonChange = (seasonId) => {
    setEditPlsSeasonId(seasonId);
    setEditForm((f) => ({ ...f, fixtureId: "", fixtureName: "", fixtureStartTime: "", fixtureEndTime: "" }));
  };

  const handleCancelEdit = () => {
    setEditingConfig(false);
    setRenderers([]);
    setEditPlsCompetitions([]);
    setEditPlsSeasons([]);
  };

  const findOrgRenderer = (rendererId) => orgRenderers.find((r) => (r._id || r.id) === rendererId);

  const handleSaveConfig = async () => {
    setSaveLoading(true);
    // A new fixture may come from another competition/season — record which
    // (ids + names), keeping any other applicationDetails keys.
    const fixtureChanged = editForm.fixtureId !== (session.fixtureId || "");
    // A stored Maestro preview URL that came from the old renderer follows
    // the renderer to its new preview renderer.
    const rendererChanged = editForm.rendererId !== (session.rendererId || "");
    const retargetedMaestro = rendererChanged && session.maestro
      ? toMaestroPayload(retargetPreviewUrl(
        maestroFormFromSession(session, "", maestroFields),
        previewRendererUrlFor(findOrgRenderer(session.rendererId)),
        previewRendererUrlFor(findOrgRenderer(editForm.rendererId)),
      ), maestroFields)
      : null;
    try {
      const res = await sessionService.updateSession(session.sessionId, {
        ...(retargetedMaestro && !isSameMaestro(retargetedMaestro, sessionMaestroPayload(session, maestroFields)) && {
          maestro: retargetedMaestro,
        }),
        ...(fixtureChanged && {
          applicationDetails: {
            ...(session.applicationDetails || {}),
            competitionId: editPlsCompetitionId || null,
            competitionName: editPlsCompetitions.find((c) => c.id === editPlsCompetitionId)?.name || null,
            seasonId: editPlsSeasonId || null,
            seasonName: editPlsSeasons.find((s) => s.id === editPlsSeasonId)?.name || null,
          },
        }),
        rendererId: editForm.rendererId || null,
        fixtureId: editForm.fixtureId || null,
        fixtureName: editForm.fixtureName || null,
        fixtureStartTime: editForm.fixtureStartTime
          ? new Date(editForm.fixtureStartTime).toISOString()
          : null,
        fixtureEndTime: editForm.fixtureEndTime
          ? new Date(editForm.fixtureEndTime).toISOString()
          : null,
      });
      setSession(res.data);
      setEditingConfig(false);
      setRenderers([]);
      showSnackbar("Session updated");
    } catch (err) {
      showSnackbar(err.message || "Failed to update session", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  // ─── Maestro setup handlers ───────────────────────────────────────────────

  const handleOpenMaestro = () => {
    setMaestroForm(maestroFormFromSession(session, previewRendererUrlFor(findOrgRenderer(session.rendererId)), maestroFields));
    setMaestroErrors({});
    setEditingMaestro(true);
  };

  const handleSaveMaestro = async () => {
    const errs = validateMaestroForm(maestroForm, maestroFields);
    setMaestroErrors(errs);
    if (Object.keys(errs).length) return;
    // null clears the setup so operators fall back to their own settings.
    const maestro = toMaestroPayload(maestroForm, maestroFields);
    if (isSameMaestro(maestro, sessionMaestroPayload(session, maestroFields))) {
      setEditingMaestro(false);
      return;
    }
    setMaestroSaving(true);
    try {
      const res = await sessionService.updateSession(session.sessionId, { maestro });
      setSession(res.data);
      setEditingMaestro(false);
      showSnackbar("Maestro setup updated");
    } catch (err) {
      showSnackbar(err.message || "Failed to update Maestro setup", "error");
    } finally {
      setMaestroSaving(false);
    }
  };

  // ─── Rename handlers ──────────────────────────────────────────────────────

  const handleStartRename = () => {
    setNameInput(getSessionDisplayName(session));
    setNameError(null);
    setEditingName(true);
  };

  const handleSaveName = async () => {
    const error = validateSessionName(nameInput);
    if (error) {
      setNameError(error);
      return;
    }
    const sessionName = nameInput.trim();
    if (sessionName === getSessionDisplayName(session)) {
      setEditingName(false);
      return;
    }
    setNameSaving(true);
    try {
      const res = await sessionService.updateSession(session.sessionId, { sessionName });
      setSession(res.data);
      setOrgSessions((prev) => prev.map((s) => (s.sessionId === session.sessionId ? { ...s, sessionName } : s)));
      setEditingName(false);
      showSnackbar("Session renamed");
    } catch (err) {
      showSnackbar(err.message || "Failed to rename session", "error");
    } finally {
      setNameSaving(false);
    }
  };

  // ─── Delete handlers ──────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await sessionService.deleteSession(session.sessionId);
      showSnackbar("Session deleted");
      setTimeout(() => navigate("/dashboard/session-management"), 1000);
    } catch (err) {
      showSnackbar(err.message || "Error deleting session", "error");
      setDeleteOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─── Unlock ───────────────────────────────────────────────────────────────

  const handleUnlock = async () => {
    try {
      const res = await sessionService.unlockSession(session.sessionId);
      setSession(res.data);
      showSnackbar("Session unlocked");
    } catch (err) {
      showSnackbar(err.message || "Error unlocking session", "error");
    }
  };

  // ─── Copy ─────────────────────────────────────────────────────────────────

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar("Copied to clipboard!");
  };

  // ─── Short URL alias ──────────────────────────────────────────────────────

  const fetchExistingAliases = async () => {
    setAliasesLoading(true);
    try {
      const res = await sessionService.listShortUrlAliases({ available: true });
      const available = res.data || [];
      if (session.shortUrl && !available.find((a) => a.slug === session.shortUrl)) {
        available.unshift({ slug: session.shortUrl, label: session.shortUrlLabel, assignedSessionId: session.sessionId });
      }
      setExistingAliases(available);
      setSelectedExisting(session.shortUrl || "");
    } catch {
      setExistingAliases([]);
    } finally {
      setAliasesLoading(false);
    }
  };

  const handleAssignAlias = async () => {
    const slug = (selectedExisting || aliasInput).trim();
    if (!slug) return;
    setShortUrlLoading(true);
    try {
      if (!selectedExisting) {
        await sessionService.createShortUrlAlias(slug, slug);
      }
      const res = await sessionService.assignShortUrl(slug, session.sessionId);
      const assignedSlug = res.data?.alias?.slug ?? slug;
      const assignedLabel = (res.data?.alias?.label ?? slug) || null;
      setSession((prev) => ({ ...prev, shortUrl: assignedSlug, shortUrlLabel: assignedLabel }));
      setAliasInput("");
      setSelectedExisting("");
      showSnackbar("Short URL assigned!");
    } catch (err) {
      showSnackbar(err.message || "Failed to assign short URL", "error");
    } finally {
      setShortUrlLoading(false);
    }
  };

  const handleUnassignAlias = async () => {
    if (!session.shortUrl) return;
    setShortUrlLoading(true);
    try {
      await sessionService.unassignShortUrl(session.shortUrl);
      setSession((prev) => ({ ...prev, shortUrl: null, shortUrlLabel: null }));
      setSelectedExisting("");
      showSnackbar("Short URL unassigned");
    } catch (err) {
      showSnackbar(err.message || "Failed to unassign short URL", "error");
    } finally {
      setShortUrlLoading(false);
    }
  };

  const handleDeleteAlias = async () => {
    if (!session.shortUrl) return;
    setShortUrlLoading(true);
    try {
      await sessionService.deleteShortUrlAlias(session.shortUrl);
      setSession((prev) => ({ ...prev, shortUrl: null, shortUrlLabel: null }));
      setSelectedExisting("");
      setDeleteAliasConfirmOpen(false);
      showSnackbar("Short URL deleted");
    } catch (err) {
      showSnackbar(err.message || "Failed to delete short URL", "error");
    } finally {
      setShortUrlLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !session) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5" color="error">
          {error || "Session not found"}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/session-management")}
          sx={{ mt: 2 }}
        >
          Back to Sessions
        </Button>
      </Box>
    );
  }

  return (
    <Grid container spacing={1} sx={{ height: "100vh" }}>
      {/* Header */}
      <Grid size={12}>
        <DashboardBreadcrumbs
          breadcrumbContent={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <MuiLink
                component={RouterLink}
                to="/dashboard/session-management"
                underline="hover"
                sx={{ color: "text.secondary", fontSize: "1.25rem", lineHeight: 1.2, fontWeight: 400 }}
              >
                Session Management
              </MuiLink>
              <Box component="span" sx={{ color: "var(--text-dim)", fontSize: "1.25rem" }}>/</Box>
              {/* Switch sessions from the breadcrumb without going back to the list. */}
              {orgSessions.length > 0 ? (
                <Select
                  value={session.sessionId}
                  onChange={(e) => navigate(`/dashboard/session-management/${e.target.value}`)}
                  variant="standard"
                  disableUnderline
                  sx={{
                    color: "text.primary", fontSize: "1.25rem", fontWeight: 600, lineHeight: 1,
                    "& .MuiSelect-select": {
                      display: "flex", alignItems: "center", py: 0, pr: "20px !important",
                    },
                    "& .MuiSvgIcon-root": { color: "var(--text-dim)" },
                  }}
                >
                  {/* Ensure the current session appears even if it fell outside the fetched page */}
                  {!orgSessions.some((s) => s.sessionId === session.sessionId) && (
                    <MenuItem value={session.sessionId} sx={{ fontSize: 13 }}>
                      {getSessionDisplayName(session)}
                    </MenuItem>
                  )}
                  {orgSessions.map((s) => (
                    <MenuItem key={s.sessionId} value={s.sessionId} sx={{ fontSize: 13 }}>
                      {getSessionDisplayName(s)}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography component="span" sx={{ color: "text.primary", fontSize: "1rem", lineHeight: 1.2, fontWeight: 600 }}>
                  {getSessionDisplayName(session)}
                </Typography>
              )}
            </Box>
          }
        />
      </Grid>

      <Grid size={12} sx={{ height: "100%" }}>
        <Box
          className="opus-scroll"
          sx={{
            height: "calc(100% - 67px)",
            width: "100%",
            overflow: "auto",
            p: 3,
            background: "var(--surface-1)",
            border: "1px solid var(--border)",
            borderRadius: "var(--r-lg)",
          }}
        >
        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {editingName ? (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <TextField
                  autoFocus
                  size="small"
                  label="Session Name"
                  value={nameInput}
                  onChange={(e) => { setNameInput(e.target.value); setNameError(null); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") setEditingName(false);
                  }}
                  error={!!nameError}
                  helperText={nameError}
                  inputProps={{ maxLength: SESSION_NAME_MAX_LENGTH }}
                  disabled={nameSaving}
                  sx={{ minWidth: 320 }}
                />
                <IconButton size="small" onClick={handleSaveName} disabled={nameSaving} sx={{ mt: 0.5 }}>
                  {nameSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={() => setEditingName(false)} disabled={nameSaving} sx={{ mt: 0.5 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="h5"
                  sx={{ color: AE_palette.seafoamGreen, wordBreak: "break-word" }}
                >
                  {getSessionDisplayName(session)}
                </Typography>
                <Tooltip title="Rename session">
                  <IconButton size="small" onClick={handleStartRename}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {session.sessionId}
              </Typography>
              <Tooltip title="Copy session ID">
                <IconButton size="small" onClick={() => handleCopy(session.sessionId)}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Chip
            label={session.status}
            color={STATUS_COLORS[session.status] || "default"}
            variant="outlined"
            size="small"
          />
          <Chip
            label={
              FLAVOUR_LABELS[session.applicationFlavour] ||
              session.applicationFlavour
            }
            variant="outlined"
            size="small"
          />
        </Box>

        <Grid container spacing={3} alignItems="flex-start">
          {/* Column 1 — Ownership */}
          <Grid size={{ xs: 12, sm: 3 }}>
            <InfoRow
              label="Organisation"
              value={orgName}
              mono
            />
            <InfoRow label="Access Mode" value={session.accessMode} />
            <InfoRow label="Created From" value={session.createdFrom} />
          </Grid>

          {/* Column 2 — Configuration */}
          <Grid size={{ xs: 12, sm: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontWeight: 600 }}
              >
                Configuration
              </Typography>
              {!editingConfig && (
                <Tooltip title="Edit renderer / fixture">
                  <IconButton size="small" onClick={handleOpenEdit}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {editingConfig ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Renderer</InputLabel>
                  <Select
                    value={editForm.rendererId}
                    label="Renderer"
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, rendererId: e.target.value }))
                    }
                    disabled={configLoading}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {renderers
                      .filter((r) => {
                        const rendererId = r._id || r.id;
                        if (rendererId === session.rendererId) return true;
                        if (r.owner_lock?.locked) return false;
                        // A renderer already "in_use" by another session is no
                        // longer excluded here: the backend now allows sharing
                        // a renderer across sessions whose fixture time windows
                        // don't overlap, and rejects with a clear error at save
                        // time (FIXTURE_TIME_CLASH) if they do.
                        return true;
                      })
                      .map((r) => (
                        <MenuItem key={r._id || r.id} value={r._id || r.id}>
                          {r.name || r._id || r.id}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <PlsCompetitionFixturePicker
                  size="small"
                  catalogue={editFixtureCatalogue}
                  competitions={editPlsCompetitions}
                  competitionId={editPlsCompetitionId}
                  onCompetitionChange={handleEditCompetitionChange}
                  seasons={editPlsSeasons}
                  seasonId={editPlsSeasonId}
                  onSeasonChange={handleEditSeasonChange}
                  // Stored names/fixture render immediately; only while the
                  // browsed competition/season is still the session's own.
                  selectedCompetitionName={
                    editPlsCompetitionId === session.applicationDetails?.competitionId
                      ? session.applicationDetails?.competitionName
                      : null
                  }
                  selectedSeasonName={
                    editPlsSeasonId === session.applicationDetails?.seasonId
                      ? session.applicationDetails?.seasonName
                      : null
                  }
                  selectedFixtureId={editForm.fixtureId}
                  selectedFixtureFallback={
                    session.fixtureId
                      ? { id: session.fixtureId, label: session.fixtureName || session.fixtureId, startDateTime: session.fixtureStartTime }
                      : null
                  }
                  onSelect={(fixtureId, label, option) => setEditForm((f) => ({
                    ...f,
                    fixtureId: fixtureId || "",
                    fixtureName: label || "",
                    // Both times are derived from the picker's selection, never
                    // typed manually — see DdsPlsCatalogue._endDateTime for how
                    // the end time falls back to start+default-duration while
                    // the fixture hasn't concluded yet.
                    fixtureStartTime: option?.startDateTime
                      ? toDateTimeLocalValue(option.startDateTime)
                      : "",
                    fixtureEndTime: option?.endDateTime
                      ? toDateTimeLocalValue(option.endDateTime)
                      : "",
                  }))}
                />
                {/* The picker drops the fixture when a Date it isn't on is
                    chosen — don't let that save the session with no fixture. */}
                {session.fixtureId && !editForm.fixtureId && (
                  <Typography sx={{ color: "error.main", fontSize: 12, mt: -0.5, ml: 1.5 }}>
                    Required
                  </Typography>
                )}

                {editForm.fixtureId && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Fixture start time"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      value={editForm.fixtureStartTime}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Fixture end time"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      value={editForm.fixtureEndTime}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveConfig}
                    disabled={saveLoading || (Boolean(session.fixtureId) && !editForm.fixtureId)}
                    startIcon={
                      saveLoading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <CheckIcon fontSize="small" />
                      )
                    }
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleCancelEdit}
                    startIcon={<CloseIcon fontSize="small" />}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <InfoRow
                  label="Renderer"
                  value={
                    session.rendererId
                      ? (rendererMap[session.rendererId] || "Renderer deleted")
                      : session.rendererId
                  }
                  mono
                />
                <InfoRow
                  label="Fixture"
                  value={
                   session.fixtureName || session.applicationDetails?.fixtureName || session.fixtureId
                  }
                  mono
                />
                {(session.fixtureStartTime || session.fixtureEndTime) && (
                  <InfoRow
                    label="Fixture window"
                    value={`${session.fixtureStartTime ? new Date(session.fixtureStartTime).toLocaleString() : "?"} – ${session.fixtureEndTime ? new Date(session.fixtureEndTime).toLocaleString() : "?"}`}
                  />
                )}
                {session.applicationDetails?.competitionId && (
                  <InfoRow
                    label="Competition"
                    value={
                      fixtureInfoLoading && !competitionName ? (
                        <CircularProgress size={12} />
                      ) : (
                        competitionName || session.applicationDetails.competitionId
                      )
                    }
                    mono={!fixtureInfoLoading && !competitionName}
                  />
                )}
                {session.applicationDetails?.seasonId && (
                  <InfoRow
                    label="Season"
                    value={
                      fixtureInfoLoading && !seasonName ? (
                        <CircularProgress size={12} />
                      ) : (
                        seasonName || session.applicationDetails.seasonId
                      )
                    }
                    mono={!fixtureInfoLoading && !seasonName}
                  />
                )}
              </>
            )}
          </Grid>

          {/* Column 3 — Audit */}
          <Grid size={{ xs: 12, sm: 3 }}>
            <InfoRow
              label="Created"
              value={new Date(session.createdAt).toLocaleString()}
            />
            <InfoRow
              label="Updated"
              value={new Date(session.updatedAt).toLocaleString()}
            />
            <InfoRow
              label="Created By"
              value={userMap[session.createdBy] || session.createdBy}
              mono
            />
            {session.lockedBy && (
              <>
                <InfoRow label="Locked By" value={session.lockedBy} mono />
                <InfoRow
                  label="Locked At"
                  value={
                    session.lockedAt
                      ? new Date(session.lockedAt).toLocaleString()
                      : null
                  }
                />
              </>
            )}
          </Grid>

          {/* Column 4 — Short URL */}
          <Grid size={{ xs: 12, sm: 2 }}>
            <Box>
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
                sx={{ mb: 0.5, fontWeight: 600 }}
              >
                Short URL
              </Typography>
              {session.shortUrl && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    {(session.shortUrlLabel || session.shortUrl) && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: AE_palette.seafoamGreen,
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {session.shortUrlLabel || session.shortUrl}
                      </Typography>
                    )}
                    {session.shortUrlLabel && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "monospace",
                          color: "text.secondary",
                          wordBreak: "break-all",
                          fontSize: "0.7rem",
                        }}
                      >
                        {buildShortUrl(session.shortUrl)}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title="Copy URL">
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleCopy(buildShortUrl(session.shortUrl));
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {isElevated && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{session.shortUrl ? "Reassign Short URL" : "Existing Short URLs"}</InputLabel>
                    <Select
                      value={selectedExisting}
                      label={session.shortUrl ? "Reassign Short URL" : "Existing Short URLs"}
                      onOpen={fetchExistingAliases}
                      onChange={(e) => { setSelectedExisting(e.target.value); setAliasInput(""); }}
                      disabled={aliasesLoading}
                    >
                      <MenuItem value=""><em>— none —</em></MenuItem>
                      {existingAliases.map((a) => (
                        <MenuItem key={a.slug} value={a.slug} sx={{ fontFamily: "monospace", fontSize: 13 }}>
                          {a.label || a.slug}
                          {a.slug === session.shortUrl && (
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>(current)</Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Divider>
                    <Typography variant="caption" color="textSecondary">OR CREATE NEW</Typography>
                  </Divider>
                  <TextField
                    size="small"
                    label="New Short URL"
                    placeholder="e.g. gallery-1"
                    value={aliasInput}
                    onChange={(e) => { setAliasInput(e.target.value); setSelectedExisting(""); }}
                    fullWidth
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="seafoamGreen"
                      fullWidth
                      onClick={handleAssignAlias}
                      disabled={shortUrlLoading || (!selectedExisting && !aliasInput.trim())}
                      startIcon={shortUrlLoading ? <CircularProgress size={14} /> : null}
                    >
                      {session.shortUrl ? "Reassign" : "Assign"}
                    </Button>
                    {session.shortUrl && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          fullWidth
                          onClick={handleUnassignAlias}
                          disabled={shortUrlLoading}
                          startIcon={shortUrlLoading ? <CircularProgress size={14} color="error" /> : null}
                        >
                          Unassign
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          fullWidth
                          onClick={() => setDeleteAliasConfirmOpen(true)}
                          disabled={shortUrlLoading}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {!isElevated && !session.shortUrl && (
                <Typography variant="body2" color="textSecondary">
                  Not assigned
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Column 5 — Actions */}
          <Grid size={{ xs: 12, sm: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {session.status === "locked" && isElevated && (
                <Button
                  variant="outlined"
                  startIcon={<LockOpenIcon />}
                  size="small"
                  onClick={handleUnlock}
                >
                  Unlock
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                size="small"
                onClick={() => setDeleteOpen(true)}
              >
                Delete
              </Button>
            </Box>
          </Grid>
        </Grid>

        {supportsMaestro(session.applicationFlavour) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                Maestro setup
              </Typography>
              {!editingMaestro && (
                <Tooltip title="Edit Maestro setup">
                  <IconButton size="small" onClick={handleOpenMaestro}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {editingMaestro ? (
              <Box sx={{ maxWidth: 960, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <MaestroSetupFields
                  fields={maestroFields}
                  value={maestroForm}
                  onChange={setMaestroForm}
                  errors={maestroErrors}
                  hasSuggestedPreview={Boolean(previewRendererUrlFor(findOrgRenderer(session.rendererId)))}
                  disabled={maestroSaving}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveMaestro}
                    disabled={maestroSaving}
                    startIcon={maestroSaving ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setEditingMaestro(false)}
                    disabled={maestroSaving}
                    startIcon={<CloseIcon fontSize="small" />}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : session.maestro ? (
              <Grid container spacing={3} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 4 }}>
                  <InfoRow label="Preview renderer URL" value={session.maestro.previewRendererUrl} mono />
                  <InfoRow
                    label="Preview source"
                    value={session.maestro.flowPreview ? previewSourceLabel(session.maestro.flowPreview.source) : "Operator’s own setting"}
                  />
                  {session.maestro.flowPreview?.source === "whep" && (
                    <>
                      <InfoRow label="WHEP endpoint" value={session.maestro.flowPreview.endpoint} mono />
                      <InfoRow label="Renderer destination" value={session.maestro.flowPreview.rendererId} mono />
                      <InfoRow label="Flow API key" value={session.maestro.flowPreview.apiKey ? "••••••••" : null} />
                    </>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <MaestroFieldSummary
                    title="Locked for operators"
                    color="var(--coral)"
                    fields={fieldsInGroup(maestroFields, "locked")}
                    values={session.maestro.locked}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <MaestroFieldSummary
                    title="Starting view · operator can change"
                    color="var(--green)"
                    fields={fieldsInGroup(maestroFields, "initial")}
                    values={session.maestro.initial}
                  />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Not set — operators use their own saved settings.
              </Typography>
            )}
          </>
        )}
        </Box>
      </Grid>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete session{" "}
            <strong>{session.sessionId}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? <CircularProgress size={14} /> : <DeleteIcon />
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete short URL confirmation dialog */}
      <Dialog
        open={deleteAliasConfirmOpen}
        onClose={() => setDeleteAliasConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Short URL</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete short URL{" "}
            <strong>{session.shortUrlLabel || session.shortUrl}</strong>? It
            will be unassigned from this session first. This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAliasConfirmOpen(false)} disabled={shortUrlLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAlias}
            color="error"
            variant="contained"
            disabled={shortUrlLoading}
            startIcon={
              shortUrlLoading ? <CircularProgress size={14} /> : <DeleteIcon />
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SessionDetails;
